import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Folder, CheckCircle, XCircle, FilePdf, ArrowLeft } from '@phosphor-icons/react';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

export function PortalDossiersPage() {
  const [searchParams] = useSearchParams();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchDossiers = async () => {
      try {
        const res = await api.get('/portal/dossiers');
        const items = res.data.items || [];
        setDossiers(items);
        const id = searchParams.get('id');
        if (id) {
          const found = items.find(d => d.id === id);
          if (found) setSelected(found);
        }
      } catch {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchDossiers();
  }, [searchParams]);

  const getStatutColor = (statut) => {
    const map = {
      brouillon: 'bg-gray-100 text-gray-800',
      ouvert: 'bg-blue-100 text-blue-800',
      complet: 'bg-emerald-100 text-emerald-800',
      en_analyse: 'bg-yellow-100 text-yellow-800',
      valide: 'bg-green-100 text-green-800',
      suspendu: 'bg-orange-100 text-orange-800',
      cloture: 'bg-gray-100 text-gray-600',
    };
    return map[statut] || 'bg-gray-100 text-gray-800';
  };

  const handleDownloadPdf = async (type, id) => {
    try {
      const res = await api.get(`/pdf/attestation/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attestation_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF telecharge');
    } catch {
      toast.error('Erreur generation PDF');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="portal-dossiers-title">Mes dossiers</h1>
        <p className="text-[#5E6D66] mt-1 text-sm">Suivi de vos dossiers et des documents requis</p>
      </div>

      {dossiers.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-12 text-center">
            <Folder size={48} className="mx-auto text-[#E8E6E1] mb-4" />
            <p className="text-[#5E6D66]">Aucun dossier en cours</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {dossiers.map((d) => {
            const cl = d.checklist || [];
            const checked = cl.filter(c => c.checked).length;
            const progress = cl.length > 0 ? Math.round((checked / cl.length) * 100) : 0;
            return (
              <Card key={d.id} className="border-[#E8E6E1] hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setSelected(d)} data-testid={`portal-dossier-card-${d.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-[#2D5A4C]/10 rounded-lg mt-0.5"><Folder size={20} className="text-[#2D5A4C]" /></div>
                      <div>
                        <p className="font-medium text-[#1A2421]">{d.numero_dossier}</p>
                        <p className="text-sm text-[#5E6D66] mt-0.5">{(d.type_dossier || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        {d.objet_besoin && <p className="text-xs text-[#5E6D66] mt-1">{d.objet_besoin}</p>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={getStatutColor(d.statut)}>{(d.statut || '').replace(/_/g, ' ')}</Badge>
                      {cl.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                            <div className="h-full bg-[#2D5A4C] rounded-full" style={{ width: `${progress}%` }} />
                          </div>
                          <span className="text-xs text-[#5E6D66]">{checked}/{cl.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dossier detail dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>
                  <div className="flex items-center gap-2">
                    <Folder size={22} className="text-[#2D5A4C]" />
                    {selected.numero_dossier}
                  </div>
                </DialogTitle>
                <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                {/* Info grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase">Type</p>
                    <p className="text-sm font-medium">{(selected.type_dossier || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase">Statut</p>
                    <Badge className={getStatutColor(selected.statut)}>{(selected.statut || '').replace(/_/g, ' ')}</Badge>
                  </div>
                  {selected.territoire && (
                    <div>
                      <p className="text-xs text-[#5E6D66] uppercase">Territoire</p>
                      <p className="text-sm">{selected.territoire}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase">Date creation</p>
                    <p className="text-sm">{selected.created_at ? new Date(selected.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                </div>

                {selected.objet_besoin && (
                  <div>
                    <p className="text-xs text-[#5E6D66] uppercase mb-1">Objet</p>
                    <p className="text-sm text-[#1A2421]">{selected.objet_besoin}</p>
                  </div>
                )}

                {/* PDF */}
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPdf('attestation', selected.id)} className="border-[#E8E6E1]" data-testid="portal-download-attestation">
                    <FilePdf size={16} className="mr-2 text-[#2D5A4C]" /> Telecharger attestation
                  </Button>
                </div>

                {/* Checklist read-only */}
                {selected.checklist && selected.checklist.length > 0 && (
                  <div className="border-t border-[#E8E6E1] pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-[#1A2421]">Documents requis</h3>
                      {(() => {
                        const cl = selected.checklist;
                        const checked = cl.filter(c => c.checked).length;
                        const pct = Math.round((checked / cl.length) * 100);
                        return (
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                              <div className="h-full bg-[#2D5A4C] rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-[#5E6D66]">{pct}%</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="space-y-2">
                      {selected.checklist.map((item) => (
                        <div
                          key={item.key}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${
                            item.checked ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#E8E6E1]'
                          }`}
                          data-testid={`portal-checklist-${item.key}`}
                        >
                          {item.checked ? (
                            <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" weight="fill" />
                          ) : (
                            <XCircle size={20} className="text-[#E8E6E1] flex-shrink-0" />
                          )}
                          <span className={`text-sm flex-1 ${item.checked ? 'line-through text-[#5E6D66]' : 'text-[#1A2421]'}`}>
                            {item.label}
                          </span>
                          {item.required && !item.checked && (
                            <Badge className="bg-red-50 text-red-600 text-[10px]">Requis</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
